import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * CSS.escape with a conservative fallback for older engines.
 * @param {string} value
 * @returns {string}
 */
function cssEscape(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}

/**
 * Quantity break selector for bundle products.
 *
 * Owns the product form's quantity, and — when a tier above 1 is selected — owns the
 * add to cart itself, so each unit in the bundle can be a different variant.
 *
 * Tier 1 is left entirely to `product-form.js`: the block's `quantity` input is
 * form-associated by id, so the native submit path works untouched. Above tier 1 the
 * submit is intercepted before the theme's delegated `document` handler sees it, and
 * the units are posted as separate lines. If this script fails to load, the native
 * path still adds N of the selected variant rather than nothing.
 *
 * @typedef {{id: number, price: number, available: boolean, options: string[], image?: string | null}} CatalogVariant
 *
 * @typedef {object} QuantityBreaksRefs
 * @property {HTMLInputElement[]} tierInputs - One radio per tier.
 * @property {HTMLInputElement} [quantityProxy] - Form-associated `quantity` input.
 *
 * @extends {Component<QuantityBreaksRefs>}
 */
class QuantityBreaksComponent extends Component {
  requiredRefs = ['tierInputs'];

  /** @type {CatalogVariant[]} */
  #variants = [];

  /** @type {string[]} */
  #optionNames = [];

  /** @type {string[][] | null} */
  #optionValues = null;

  /** @type {number} */
  #activeTier = 1;

  /** @type {number | null} */
  #lastUnitPrice = null;

  /** @type {AbortController | null} */
  #listeners = null;

  /** True while a change to item #1 is being pushed into the main variant picker. */
  #pushingToMain = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pushTimer;

  /**
   * Design-grid picks per tier (see "design grid" below). `touched` turns true at the
   * shopper's first tap, after which the main picker stops reseeding the picks.
   * @type {Map<number, {values: string[], touched: boolean}>}
   */
  #designPicks = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#variants = this.#parse('variants', []);
    this.#optionNames = this.#parse('optionNames', []);
    this.#activeTier = this.#selectedQuantity();
    this.#lastUnitPrice = this.#currentUnitPrice();

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    for (const form of this.#productForms) {
      // Bound on the component, not the <form>: it survives the section morph, and it
      // still runs before the theme's delegated listener on `document`.
      form.addEventListener('submit', this.#onSubmit, { signal });
    }

    // A ticked upsell offer means there are extra lines to post, which this component
    // has to own the submit to do — see #syncSubmitOwnership.
    this.#scope.addEventListener('upsell:change', () => this.#syncSubmitOwnership(), { signal });

    // A variant change morphs the picker in place; re-read once the frame settles.
    this.#scope.addEventListener(
      'change',
      () => requestAnimationFrame(() => this.#onVariantMaybeChanged()),
      { signal }
    );

    // The variant picker re-renders the section after a fetch, so the 'change' above can
    // run before the new variant is in the form. Its select event carries a promise that
    // settles once the page shows the new variant; re-read then too.
    document.addEventListener(
      'shopify:product:select',
      (event) => {
        if (!(event.target instanceof Node) || !this.#scope.contains(event.target)) return;
        const settled = /** @type {any} */ (event).promise;
        Promise.resolve(settled)
          .catch(() => {})
          .finally(() => requestAnimationFrame(() => this.#onVariantMaybeChanged()));
      },
      { signal }
    );

    this.#applyQuantity(this.#activeTier);
    this.#syncSubmitOwnership();
    this.#syncPicks();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = null;
    this.#activeTier = 1;
    this.#syncSubmitOwnership();
  }

  /**
   * Declarative `on:change` handler for the tier radios.
   * @param {Event} event
   */
  selectTier(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.checked) return;
    this.#activeTier = Number(input.value) || 1;
    this.#applyQuantity(this.#activeTier);
    this.#syncSubmitOwnership();
    this.#syncPicks();
  }

  /**
   * @param {string} key
   * @param {any} fallback
   */
  #parse(key, fallback) {
    try {
      return JSON.parse(this.dataset[key] || '');
    } catch {
      return fallback;
    }
  }

  get #scope() {
    return this.closest('.shopify-section') ?? document;
  }

  /** @returns {HTMLElement[]} */
  get #productForms() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    if (!productId) return [];
    const selector = `product-form-component[data-product-id="${productId}"]${
      sectionId ? `[data-section-id="${sectionId}"]` : ''
    }`;
    return /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll(selector)));
  }

  /** Distinct values per option, in catalog order. */
  get #valuesByOption() {
    if (!this.#optionValues) {
      this.#optionValues = this.#optionNames.map((_name, index) => {
        /** @type {string[]} */
        const seen = [];
        for (const variant of this.#variants) {
          const value = variant.options?.[index];
          if (value != null && !seen.includes(value)) seen.push(value);
        }
        return seen;
      });
    }
    return this.#optionValues;
  }

  /** @returns {number} */
  #selectedQuantity() {
    const checked = this.refs.tierInputs.find((input) => input.checked);
    return Number(checked?.value) || 1;
  }

  /** @returns {string | null} */
  #currentVariantId() {
    const [form] = this.#productForms;
    const input = form?.querySelector('input[name="id"]');
    return input instanceof HTMLInputElement && input.value ? input.value : null;
  }

  /** @returns {CatalogVariant | undefined} */
  #currentVariant() {
    const id = this.#currentVariantId();
    return id ? this.#variants.find((variant) => String(variant.id) === id) : undefined;
  }

  /** @returns {number | null} */
  #currentUnitPrice() {
    const variant = this.#currentVariant();
    return typeof variant?.price === 'number' ? variant.price : null;
  }

  /**
   * Writes the tier quantity into every matching product form.
   * @param {number} quantity
   */
  #applyQuantity(quantity) {
    const proxy = this.refs.quantityProxy;

    for (const form of this.#productForms) {
      const native = form.querySelector('quantity-selector-component');
      const nativeInput = form.querySelector('input[name="quantity"]');

      let next = quantity;
      if (nativeInput instanceof HTMLInputElement) {
        const max = Number(nativeInput.max);
        if (Number.isFinite(max) && max > 0) next = Math.min(next, max);
      }

      if (native) {
        // A rendered stepper is the form's own control. Defer to it and stand down,
        // so FormData never sees two `quantity` fields.
        if (proxy) proxy.disabled = true;
        if (nativeInput instanceof HTMLInputElement) nativeInput.value = String(next);
        if (typeof (/** @type {any} */ (native).setValue) === 'function') {
          /** @type {any} */ (native).setValue(String(next));
        }
      } else if (proxy) {
        proxy.disabled = false;
        proxy.value = String(next);
      }

      form.dataset.quantityDefault = String(next);
    }
  }

  // --- per-unit variant pickers -------------------------------------------

  /** Shows the picker block belonging to the active tier, building it on first use. */
  #syncPicks() {
    for (const picks of this.querySelectorAll('.quantity-breaks__picks')) {
      if (!(picks instanceof HTMLElement)) continue;
      const tier = Number(picks.dataset.picksFor);
      const show = tier === this.#activeTier;
      if (show && !picks.dataset.rendered) this.#buildPicks(picks, tier);
      picks.hidden = !show;
    }
    this.#syncRowOneFromVariant();
    this.#validate();
  }

  /**
   * @param {HTMLElement} container
   * @param {number} tier
   */
  #buildPicks(container, tier) {
    if (this.#isDesignGrid) {
      this.#buildDesignGrid(container, tier);
      return;
    }

    const unitLabel = this.dataset.unitLabel || 'item';

    // Two or more dropdowns per unit read as a jumble on one wrapping line, so those
    // products get one card per unit instead (see #buildStackedRow).
    const stacked = this.#optionNames.length >= 2 && !this.querySelector('template[data-qb-swatches]');

    if (!stacked) {
      const caption = document.createElement('p');
      caption.className = 'quantity-breaks__picks-first';
      caption.textContent = this.#optionNames.join(', ');
      container.appendChild(caption);
    }

    // Item #1 gets a row too, and stays in step with the main picker above.
    for (let unit = 1; unit <= tier; unit++) {
      if (stacked) {
        container.appendChild(this.#buildStackedRow(unit, unitLabel));
        continue;
      }

      const row = document.createElement('div');
      row.className = 'quantity-breaks__pick';
      row.dataset.unit = String(unit);

      const label = document.createElement('span');
      label.className = 'quantity-breaks__pick-label';
      label.textContent = `#${unit}`;
      row.appendChild(label);

      this.#optionNames.forEach((name, index) => {
        // A colour name spells nothing: if the block rendered tiles for this option,
        // use them so the shopper sees the garment rather than the word.
        const template = this.querySelector(`template[data-qb-swatches="${index}"]`);
        if (template instanceof HTMLTemplateElement) {
          row.appendChild(this.#buildSwatchGroup(template, index, name, unit, unitLabel));
          return;
        }

        // Designs keep the dropdown but show a small photo of the chosen design beside it.
        if (this.#imagesByOption(index)) {
          const frame = document.createElement('span');
          frame.className = 'quantity-breaks__pick-thumb';
          frame.dataset.thumbIndex = String(index);
          const thumb = document.createElement('img');
          thumb.alt = '';
          thumb.width = 44;
          thumb.height = 44;
          thumb.loading = 'lazy';
          frame.appendChild(thumb);
          row.appendChild(frame);
        }

        const select = document.createElement('select');
        select.dataset.optionIndex = String(index);
        select.setAttribute('aria-label', `${unitLabel} ${unit} — ${name}`);
        for (const value of this.#valuesByOption[index] ?? []) {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        }
        select.addEventListener('change', () => {
          this.#syncThumb(row, index);
          this.#onUnitChanged(row);
        });
        row.appendChild(select);
      });

      const error = document.createElement('p');
      error.className = 'quantity-breaks__pick-error';
      error.hidden = true;
      row.appendChild(error);

      container.appendChild(row);
    }

    container.dataset.rendered = '1';
    this.#defaultPicksToCurrent(container);
  }

  /**
   * One unit as a card: the photo of exactly what is picked (style and colour, not just
   * the first option) with the unit number on it, and one dropdown per option beside it.
   * @param {number} unit
   * @param {string} unitLabel
   * @returns {HTMLElement}
   */
  #buildStackedRow(unit, unitLabel) {
    const row = document.createElement('div');
    row.className = 'quantity-breaks__pick quantity-breaks__pick--stacked';
    row.dataset.unit = String(unit);

    const photo = document.createElement('span');
    photo.className = 'quantity-breaks__pick-photo';
    const image = document.createElement('img');
    image.alt = '';
    image.width = 72;
    image.height = 72;
    image.loading = 'lazy';
    photo.appendChild(image);
    const number = document.createElement('span');
    number.className = 'quantity-breaks__pick-number';
    number.textContent = String(unit);
    photo.appendChild(number);
    row.appendChild(photo);

    const fields = document.createElement('div');
    fields.className = 'quantity-breaks__pick-fields';
    this.#optionNames.forEach((name, index) => {
      const select = document.createElement('select');
      select.dataset.optionIndex = String(index);
      select.setAttribute('aria-label', `${unitLabel} ${unit} — ${name}`);
      for (const value of this.#valuesByOption[index] ?? []) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      }
      select.addEventListener('change', () => this.#onUnitChanged(row));
      fields.appendChild(select);
    });
    row.appendChild(fields);

    const error = document.createElement('p');
    error.className = 'quantity-breaks__pick-error';
    error.hidden = true;
    row.appendChild(error);

    return row;
  }

  /**
   * Points a card's photo at the variant it is set to. A combination that does not exist
   * (no white no-show) falls back to the closest one by leading options, so the photo
   * still shows the right style.
   * @param {Element} row
   */
  #syncPairPhoto(row) {
    const photo = row.querySelector('.quantity-breaks__pick-photo');
    const image = photo?.querySelector('img');
    if (!(photo instanceof HTMLElement) || !(image instanceof HTMLImageElement)) return;

    const chosen = this.#rowValues(row);
    /** @type {string | undefined} */
    let src;
    for (let depth = chosen.length; depth > 0 && !src; depth--) {
      const lead = chosen.slice(0, depth);
      const match = this.#variants.find(
        (variant) => variant.image && lead.every((value, index) => variant.options?.[index] === value)
      );
      src = match?.image ?? undefined;
    }

    if (src && image.getAttribute('src') !== src) image.src = src;
    photo.dataset.empty = String(!src);
  }

  /**
   * @param {HTMLTemplateElement} template
   * @param {number} index
   * @param {string} name
   * @param {number} unit
   * @param {string} unitLabel
   * @returns {HTMLElement}
   */
  #buildSwatchGroup(template, index, name, unit, unitLabel) {
    const wrap = document.createElement('div');
    wrap.className = 'quantity-breaks__pick-color';

    const group = document.createElement('div');
    group.className = 'quantity-breaks__swatches';
    group.dataset.optionIndex = String(index);
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', `${unitLabel} ${unit} — ${name}`);
    group.appendChild(template.content.cloneNode(true));

    for (const button of group.querySelectorAll('.quantity-breaks__swatch')) {
      button.addEventListener('click', () => this.#chooseSwatch(group, button.dataset.value ?? ''));
    }

    wrap.appendChild(group);
    return wrap;
  }

  /**
   * @param {HTMLElement} group
   * @param {string} value
   */
  #chooseSwatch(group, value, silent = false) {
    group.dataset.chosen = value;

    for (const button of group.querySelectorAll('.quantity-breaks__swatch')) {
      if (!(button instanceof HTMLElement)) continue;
      const selected = button.dataset.value === value;
      button.dataset.selected = String(selected);
      button.setAttribute('aria-pressed', String(selected));
    }

    if (!silent) this.#onUnitChanged(group.closest('.quantity-breaks__pick'));
  }

  /**
   * @param {Element | null} row
   */
  #onUnitChanged(row) {
    if (row instanceof HTMLElement && row.dataset.unit === '1') this.#pushRowOneToMainPicker(row);
    this.#validate();
  }

  /**
   * Item #1 is the variant the rest of the page is showing, so changing it here
   * drives the main picker — which then updates the gallery, price and URL the
   * way it would if the shopper had clicked it directly.
   * @param {HTMLElement} row
   */
  #pushRowOneToMainPicker(row) {
    const values = this.#rowValues(row);
    const scope = this.#scope;
    let clicked = false;

    this.#optionNames.forEach((name, index) => {
      const value = values[index];
      if (!value) return;
      const input = scope.querySelector(
        `variant-picker input[type="radio"][data-option-name="${cssEscape(name)}"][value="${cssEscape(value)}"]`
      );
      if (input instanceof HTMLInputElement && !input.checked) {
        input.click();
        clicked = true;
      }
    });

    if (!clicked) return;

    // That refetches the section; hold off writing item #1 back from the variant
    // until it settles, or a two-option change stomps itself halfway through.
    this.#pushingToMain = true;
    clearTimeout(this.#pushTimer);
    this.#pushTimer = setTimeout(() => {
      this.#pushingToMain = false;
    }, 1200);
  }

  /**
   * @param {Element} row
   * @param {string[]} values
   */
  #setRowValues(row, values) {
    this.#optionNames.forEach((_name, index) => {
      const value = values[index];
      if (value == null) return;

      const select = row.querySelector(`select[data-option-index="${index}"]`);
      if (select instanceof HTMLSelectElement) {
        select.value = value;
        this.#syncThumb(row, index);
        return;
      }

      const group = row.querySelector(`.quantity-breaks__swatches[data-option-index="${index}"]`);
      if (group instanceof HTMLElement) this.#chooseSwatch(group, value, true);
    });
    this.#syncPairPhoto(row);
  }

  /**
   * Photo per value for a design option, from the block's data-qb-images JSON.
   * @param {number} index
   * @returns {Record<string, string> | null}
   */
  #imagesByOption(index) {
    const script = this.querySelector(`script[data-qb-images="${index}"]`);
    if (!script) return null;
    try {
      const images = JSON.parse(script.textContent || '{}');
      return images && typeof images === 'object' && Object.keys(images).length ? images : null;
    } catch {
      return null;
    }
  }

  /**
   * Points a unit's design photo at whatever its dropdown is set to.
   * @param {Element} row
   * @param {number} index
   */
  #syncThumb(row, index) {
    const frame = row.querySelector(`[data-thumb-index="${index}"]`);
    const thumb = frame?.querySelector('img');
    const select = row.querySelector(`select[data-option-index="${index}"]`);
    if (!(frame instanceof HTMLElement) || !(thumb instanceof HTMLImageElement) || !(select instanceof HTMLSelectElement)) return;
    const src = this.#imagesByOption(index)?.[select.value];
    if (src) {
      thumb.src = src;
      frame.title = select.value;
    }
    frame.style.visibility = src ? 'visible' : 'hidden';
  }

  /** Mirrors the page's selected variant back into item #1. */
  #syncRowOneFromVariant() {
    if (this.#isDesignGrid) {
      this.#reseedDesignGrid();
      return;
    }

    const current = this.#currentVariant();
    const row = this.#activePicks?.querySelector('.quantity-breaks__pick[data-unit="1"]');
    if (current && row) this.#setRowValues(row, current.options ?? []);
  }

  /**
   * The chosen value per option for a unit, whichever control renders it.
   * @param {Element} row
   * @returns {string[]}
   */
  #rowValues(row) {
    return this.#optionNames.map((_name, index) => {
      const select = row.querySelector(`select[data-option-index="${index}"]`);
      if (select instanceof HTMLSelectElement) return select.value;
      const group = row.querySelector(`.quantity-breaks__swatches[data-option-index="${index}"]`);
      return group instanceof HTMLElement ? group.dataset.chosen ?? '' : '';
    });
  }

  /**
   * Seeds every unit with whatever is selected above, so the default bundle is
   * "3 of what I'm looking at" and the shopper only changes what they want changed.
   * @param {HTMLElement} container
   */
  #defaultPicksToCurrent(container) {
    const current = this.#currentVariant();
    if (!current) return;

    for (const row of container.querySelectorAll('.quantity-breaks__pick')) {
      this.#setRowValues(row, current.options ?? []);
    }
  }

  /**
   * @param {Element} row
   * @returns {CatalogVariant | undefined}
   */
  #resolveRow(row) {
    const chosen = this.#rowValues(row);
    if (!chosen.length || chosen.some((value) => value === '')) return undefined;
    return this.#variants.find((variant) =>
      chosen.every((value, index) => variant.options?.[index] === value)
    );
  }

  /** @returns {string} */
  get #soldOutLabel() {
    return this.dataset.soldOutLabel || 'Sold out';
  }

  /**
   * True when at least one in-stock variant matches every non-blank entry in
   * `values`. A blank entry means "that option is not chosen yet", so it matches
   * anything — otherwise a half-filled row would report everything as sold out.
   * @param {string[]} values
   * @returns {boolean}
   */
  #combinationAvailable(values) {
    return this.#variants.some(
      (variant) =>
        variant.available &&
        values.every((value, index) => value === '' || variant.options?.[index] === value)
    );
  }

  /**
   * Marks the option values this unit cannot actually buy.
   *
   * Availability is a fact about a *combination*, not a value: Gold is sold out in
   * 3.5mm and in stock in USB-C. So this re-probes every value against the unit's
   * other current choices, and has to re-run on every change rather than once at
   * build time.
   *
   * Nothing is hard-disabled — see the CSS note in blocks/quantity-breaks.liquid.
   * The original labels are stashed on first pass so repeated runs stay idempotent.
   * @param {Element} row
   */
  #markRowAvailability(row) {
    const chosen = this.#rowValues(row);

    this.#optionNames.forEach((_name, index) => {
      /** @param {string} value */
      const probe = (value) => {
        const candidate = chosen.slice();
        candidate[index] = value;
        return this.#combinationAvailable(candidate);
      };

      const select = row.querySelector(`select[data-option-index="${index}"]`);
      if (select instanceof HTMLSelectElement) {
        for (const option of select.options) {
          if (option.dataset.label == null) option.dataset.label = option.textContent ?? '';
          const label = option.dataset.label;
          const ok = probe(option.value);
          option.dataset.available = String(ok);
          option.textContent = ok ? label : `${label} — ${this.#soldOutLabel}`;
        }
        select.dataset.soldOut = String(!probe(select.value));
        return;
      }

      const group = row.querySelector(`.quantity-breaks__swatches[data-option-index="${index}"]`);
      if (!(group instanceof HTMLElement)) return;

      for (const button of group.querySelectorAll('.quantity-breaks__swatch')) {
        if (!(button instanceof HTMLElement)) continue;
        const value = button.dataset.value ?? '';
        if (button.dataset.label == null) button.dataset.label = button.title || value;
        const label = button.dataset.label;
        const ok = probe(value);
        button.dataset.available = String(ok);
        button.setAttribute('aria-disabled', String(!ok));
        button.title = ok ? label : `${label} — ${this.#soldOutLabel}`;
        // The tile is a photo; the screen reader name is the only thing carrying state.
        const spoken = button.querySelector('.visually-hidden');
        if (spoken) spoken.textContent = button.title;
      }
    });
  }

  /**
   * Offers ticked in the optional upsell block sharing this section.
   *
   * The upsell component deliberately does not add to cart while this component is
   * present: both would intercept the same submit and the shopper would get two adds.
   * It exposes its picks instead and this component posts everything in one request.
   *
   * @returns {Array<{id: string, quantity: number}>}
   */
  #upsellItems() {
    const upsell = this.#scope.querySelector('upsell-checks-component');
    const items = /** @type {any} */ (upsell)?.selectedItems;
    return Array.isArray(items) ? items : [];
  }

  get #activePicks() {
    return this.querySelector(`.quantity-breaks__picks[data-picks-for="${this.#activeTier}"]`);
  }

  /**
   * @param {boolean} [reveal] On a submit attempt: show what is missing and bring it into view.
   * @returns {boolean} True when every unit resolves to a buyable variant.
   */
  #validate(reveal = false) {
    const container = this.#activePicks;
    if (!container) return true;
    if (container instanceof HTMLElement && container.classList.contains('quantity-breaks__picks--designs')) {
      return this.#validateDesignGrid(container, reveal);
    }

    let valid = true;
    for (const row of container.querySelectorAll('.quantity-breaks__pick')) {
      this.#markRowAvailability(row);
      this.#syncPairPhoto(row);
      const variant = this.#resolveRow(row);
      const bad = !variant || !variant.available;
      const error = row.querySelector('.quantity-breaks__pick-error');
      if (error instanceof HTMLElement) {
        error.hidden = !bad;
        error.textContent = bad ? this.dataset.unavailableLabel || 'Unavailable' : '';
      }
      if (bad) valid = false;
    }
    return valid;
  }

  #onVariantMaybeChanged() {
    this.#syncSubmitOwnership();
    if (!this.#pushingToMain) this.#syncRowOneFromVariant();
    this.#refreshPrices();
  }

  // --- design grid ---------------------------------------------------------
  //
  // A product whose only option is a printed design (the metal wallets) picks its
  // units from a grid of photos, the way the charm builder picks charms: tap a design
  // to add it, tap a pick to take it out again. A dropdown per unit showed shoppers
  // names they could not picture. The picks live in #designPicks; #collectItems
  // turns them into cart lines.

  /** What the grid picks, from the option name: "design" for Design, "model" for Model. */
  get #pickNoun() {
    return (this.#optionNames[0] || 'design').trim().toLowerCase();
  }

  /** True when units are picked from the photo grid rather than per-unit dropdowns. */
  get #isDesignGrid() {
    return this.#optionNames.length === 1 && Boolean(this.#imagesByOption(0));
  }

  /**
   * The picks for a tier, seeded with the design on screen.
   * @param {number} tier
   */
  #designState(tier) {
    const existing = this.#designPicks.get(tier);
    if (existing) return existing;
    const current = this.#currentVariant()?.options?.[0];
    const seeded = { values: current ? [current] : [], touched: false };
    this.#designPicks.set(tier, seeded);
    return seeded;
  }

  /**
   * @param {number} tier
   * @param {string[]} values
   */
  #setDesignPicks(tier, values) {
    this.#designPicks.set(tier, { values, touched: true });
  }

  /**
   * @param {string} value
   * @returns {CatalogVariant | undefined}
   */
  #variantForDesign(value) {
    return this.#variants.find((variant) => variant.options?.[0] === value);
  }

  /**
   * @param {HTMLElement} container
   * @param {number} tier
   */
  #buildDesignGrid(container, tier) {
    const images = this.#imagesByOption(0) ?? {};
    container.classList.add('quantity-breaks__picks--designs');
    container.innerHTML = `
      <div class="qb-designs__head">
        <span class="qb-designs__title"></span>
        <span class="qb-designs__count" data-qb-count></span>
      </div>
      <div class="qb-designs__meter" aria-hidden="true"><i data-qb-meter></i></div>
      <div class="qb-designs__picked" data-qb-picked></div>
      <p class="qb-designs__note" role="status" data-qb-note></p>
      <div class="qb-designs__grid" data-qb-grid></div>
      <div class="qb-designs__actions">
        <button type="button" class="qb-designs__ghost" data-qb-surprise>Surprise me</button>
        <button type="button" class="qb-designs__ghost qb-designs__ghost--quiet" data-qb-clear>Clear all</button>
      </div>`;
    const noun = this.#pickNoun;
    const title = container.querySelector('.qb-designs__title');
    if (title) title.textContent = `Pick your ${tier} ${noun}s`;
    const picked = container.querySelector('[data-qb-picked]');
    if (picked instanceof HTMLElement) {
      picked.setAttribute('aria-label', `Your ${noun}s`);
      picked.dataset.empty = `No ${noun}s yet. Tap a ${noun} to add it.`;
    }

    const grid = container.querySelector('[data-qb-grid]');
    for (const value of this.#valuesByOption[0] ?? []) {
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'qb-designs__tile';
      tile.dataset.value = value;
      tile.setAttribute('aria-label', `Add ${value}`);

      const qty = document.createElement('span');
      qty.className = 'qb-designs__qty';
      qty.hidden = true;

      const face = document.createElement('span');
      face.className = 'qb-designs__face';
      if (images[value]) face.append(this.#designImage(images[value], 120));

      const name = document.createElement('span');
      name.className = 'qb-designs__name';
      name.textContent = value;

      tile.append(qty, face, name);
      grid?.append(tile);
    }

    container.addEventListener('click', (event) => this.#onDesignClick(event, container, tier));
    container.dataset.rendered = '1';
    this.#renderDesignGrid(container, tier);
  }

  /**
   * @param {string} src
   * @param {number} size
   */
  #designImage(src, size) {
    const image = document.createElement('img');
    image.src = src;
    image.alt = '';
    image.width = size;
    image.height = size;
    image.loading = 'lazy';
    return image;
  }

  /**
   * Taps inside the grid: add a design, remove a pick, surprise me, clear all.
   * @param {Event} event
   * @param {HTMLElement} container
   * @param {number} tier
   */
  #onDesignClick(event, container, tier) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const { values } = this.#designState(tier);

    const tile = target.closest('.qb-designs__tile');
    const remove = target.closest('[data-qb-remove]');
    if (tile instanceof HTMLElement) {
      const value = tile.dataset.value ?? '';
      if (values.length >= tier) {
        this.#designNote(container, tier, true);
        return;
      }
      if (!this.#variantForDesign(value)?.available) return;
      this.#setDesignPicks(tier, [...values, value]);
    } else if (remove instanceof HTMLElement) {
      const index = Number(remove.dataset.qbRemove);
      this.#setDesignPicks(tier, values.filter((_value, position) => position !== index));
    } else if (target.closest('[data-qb-surprise]')) {
      const pool = (this.#valuesByOption[0] ?? []).filter(
        (value) => !values.includes(value) && this.#variantForDesign(value)?.available
      );
      const added = [];
      while (values.length + added.length < tier && pool.length) {
        added.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
      }
      this.#setDesignPicks(tier, [...values, ...added]);
    } else if (target.closest('[data-qb-clear]')) {
      this.#setDesignPicks(tier, []);
    } else {
      return;
    }

    this.#renderDesignGrid(container, tier);
  }

  /**
   * @param {HTMLElement} container
   * @param {number} tier
   */
  #renderDesignGrid(container, tier) {
    const { values } = this.#designState(tier);
    const images = this.#imagesByOption(0) ?? {};

    const count = container.querySelector('[data-qb-count]');
    if (count) count.innerHTML = `<b>${values.length}</b> / ${tier} picked`;
    const meter = container.querySelector('[data-qb-meter]');
    if (meter instanceof HTMLElement) meter.style.width = `${Math.min(100, (values.length / tier) * 100)}%`;

    const picked = container.querySelector('[data-qb-picked]');
    picked?.replaceChildren(
      ...values.map((value, index) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.dataset.qbRemove = String(index);
        chip.setAttribute('aria-label', `Remove ${value}`);
        if (images[value]) chip.append(this.#designImage(images[value], 24));
        const label = document.createElement('span');
        label.textContent = value;
        chip.append(label);
        return chip;
      })
    );

    const full = values.length >= tier;
    for (const tile of container.querySelectorAll('.qb-designs__tile')) {
      if (!(tile instanceof HTMLElement)) continue;
      const times = values.filter((value) => value === tile.dataset.value).length;
      const qty = tile.querySelector('.qb-designs__qty');
      if (qty instanceof HTMLElement) {
        qty.hidden = times === 0;
        qty.textContent = String(times);
      }
      tile.classList.toggle('is-picked', times > 0);
      tile.classList.toggle('is-full', full);
      tile.setAttribute('aria-disabled', String(full));
    }

    this.#designNote(container, tier, false);
  }

  /**
   * The line under the picks: how many are still missing, or how to swap one.
   * @param {HTMLElement} container
   * @param {number} tier
   * @param {boolean} emphasise Red, for a tap or a submit that could not go through.
   */
  #designNote(container, tier, emphasise) {
    const note = container.querySelector('[data-qb-note]');
    if (!(note instanceof HTMLElement)) return;
    const left = tier - this.#designState(tier).values.length;
    note.textContent =
      left > 0
        ? `Pick ${left} more ${this.#pickNoun}${left === 1 ? '' : 's'}.`
        : `All ${tier} picked. Tap a pick above to remove it and swap.`;
    note.classList.toggle('is-error', emphasise);
  }

  /**
   * @param {HTMLElement} container
   * @param {boolean} reveal
   */
  #validateDesignGrid(container, reveal) {
    const tier = this.#activeTier;
    const { values } = this.#designState(tier);
    const valid = values.length === tier && values.every((value) => this.#variantForDesign(value)?.available);
    if (reveal && !valid) {
      this.#designNote(container, tier, true);
      container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return valid;
  }

  /** Until the shopper taps the grid, its first pick follows the design on screen. */
  #reseedDesignGrid() {
    const container = this.#activePicks;
    const state = this.#designPicks.get(this.#activeTier);
    const current = this.#currentVariant()?.options?.[0];
    if (!(container instanceof HTMLElement) || !state || state.touched || !current) return;
    if (state.values.length === 1 && state.values[0] === current) return;
    this.#designPicks.set(this.#activeTier, { values: [current], touched: false });
    this.#renderDesignGrid(container, this.#activeTier);
  }

  // --- add to cart ---------------------------------------------------------

  /**
   * Hands submit ownership back and forth with `product-form.js`.
   *
   * The theme routes `on:` handlers from a single delegated listener on `document`
   * registered with `{ capture: true }`, so it fires before any listener on the form
   * or the component — `stopPropagation` from here can never reach it. What it does
   * respect is `getElement()`, which resolves the handler via
   * `target.closest('[on:submit]')`. Dropping that attribute while a multi-unit tier
   * is selected makes the delegated listener find nothing and return, leaving this
   * component to handle the submit. Tier 1 gets the attribute back and the native
   * path runs untouched.
   */
  #syncSubmitOwnership() {
    const owned = this.#activeTier > 1 || this.#upsellItems().length > 0;

    for (const form of this.#productForms) {
      if (owned) {
        // The section morph re-adds the attribute from the server HTML, so re-check
        // rather than assuming a single removal sticks.
        if (form.hasAttribute('on:submit')) {
          form.dataset.qbSubmit = form.getAttribute('on:submit') ?? '/handleSubmit';
          form.removeAttribute('on:submit');
        }
      } else if (form.dataset.qbSubmit != null) {
        form.setAttribute('on:submit', form.dataset.qbSubmit);
        delete form.dataset.qbSubmit;
      }
    }
  }

  /**
   * @param {Event} event
   */
  #onSubmit = (event) => {
    // Tier 1 with nothing ticked is an ordinary single-variant add; leave the theme to it.
    if (this.#activeTier <= 1 && this.#upsellItems().length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    if (!this.#validate(true)) return;

    const items = this.#collectItems();
    if (!items) return;

    void this.#addItems(items, event);
  };

  /** @returns {Array<{id: string, quantity: number}> | null} */
  #collectItems() {
    /** @type {string[]} */
    const ids = [];
    const container = this.#activePicks;

    if (container instanceof HTMLElement && container.classList.contains('quantity-breaks__picks--designs')) {
      for (const value of this.#designState(this.#activeTier).values) {
        const variant = this.#variantForDesign(value);
        if (!variant || !variant.available) return null;
        ids.push(String(variant.id));
      }
    } else if (container) {
      for (const row of container.querySelectorAll('.quantity-breaks__pick')) {
        const variant = this.#resolveRow(row);
        if (!variant || !variant.available) return null;
        ids.push(String(variant.id));
      }
    } else {
      // No options to pick: the bundle is just N of the same thing.
      const current = this.#currentVariantId();
      if (!current) return null;
      for (let i = 0; i < this.#activeTier; i++) ids.push(current);
    }

    if (!ids.length) return null;

    /** @type {Map<string, number>} */
    const counts = new Map();
    for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);

    // Offers merge by id, so ticking an accessory the bundle already contains bumps
    // that line rather than posting it twice.
    for (const item of this.#upsellItems()) {
      counts.set(item.id, (counts.get(item.id) ?? 0) + item.quantity);
    }

    return Array.from(counts, ([id, quantity]) => ({ id, quantity }));
  }

  /**
   * Posts the bundle as separate lines and tells the rest of the theme, so the cart
   * drawer, cart icon and sticky bar update exactly as they do for a normal add.
   * @param {Array<{id: string, quantity: number}>} items
   * @param {Event} event
   */
  async #addItems(items, event) {
    const [form] = this.#productForms;
    const buttons = Array.from(document.querySelectorAll('product-form-component add-to-cart-component'));
    const cartSections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : null))
      .filter(Boolean);

    for (const button of buttons) /** @type {any} */ (button).disable?.();

    const deferred = CartLinesUpdateEvent.createPromise();
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    (form ?? this).dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: item.id, quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({ id: Number(item.id), quantity: item.quantity })),
          sections: cartSections.join(','),
        }),
      }).then((res) => res.json());

      if (response?.status) throw new Error(response.message || 'Add to cart failed');

      const cart = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'quantity-breaks-component',
          sourceId: this.dataset.productId ?? '',
          itemCount,
          productId: this.dataset.productId,
          // The cart markup came back with the add; without it the cart re-fetches it.
          sections: response.sections,
          didError: false,
        },
      });

      for (const button of buttons) /** @type {any} */ (button).animateAddToCart?.();
    } catch (error) {
      console.error('[quantity-breaks]', error);
      deferred.reject(error);
    } finally {
      for (const button of buttons) /** @type {any} */ (button).enable?.();
      if (event) event.target?.dispatchEvent?.(new Event('qb:add-complete', { bubbles: true }));
    }
  }

  // --- price rendering -----------------------------------------------------

  /** Re-renders tier prices when the selected variant costs something different. */
  #refreshPrices() {
    const unitPrice = this.#currentUnitPrice();
    if (unitPrice == null || unitPrice === this.#lastUnitPrice) return;
    this.#lastUnitPrice = unitPrice;

    /** @type {Record<string, number>} Exact row totals in cents, keyed by quantity. */
    const tierPrices = this.#parse('tierPrices', {});
    if (!tierPrices || typeof tierPrices !== 'object') return;

    const saveLabel = this.dataset.saveLabel ?? 'Save';

    for (const input of this.refs.tierInputs) {
      const tier = input.closest('.quantity-breaks__tier');
      if (!tier) continue;

      const quantity = Number(input.value) || 1;
      // The row price is fixed per product (bundle.tiers), so only the
      // "Save" figure moves with the selected variant's own price.
      const lineTotal = Number(tierPrices[quantity]);
      if (!Number.isFinite(lineTotal)) continue;
      const saved = Math.max(0, unitPrice * quantity - lineTotal);

      const priceEl = tier.querySelector('.quantity-breaks__price');
      if (priceEl) priceEl.textContent = this.#money(lineTotal);

      const subEl = tier.querySelector('.quantity-breaks__sub');
      if (subEl) subEl.textContent = saved > 0 ? `${saveLabel} ${this.#money(saved)}` : '';
    }
  }

  /**
   * Formats cents using the shop's money format.
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name) => {
      switch (name) {
        case 'amount_no_decimals':
          return formatNumber(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return formatNumber(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return formatNumber(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return formatNumber(cents, 2, "'", '.');
        case 'amount_with_space_separator':
          return formatNumber(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return formatNumber(cents, 0, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return formatNumber(cents, 2, ' ', '.');
        default:
          return formatNumber(cents, 2, ',', '.');
      }
    });
  }
}

/**
 * @param {number} cents
 * @param {number} decimals
 * @param {string} thousands
 * @param {string} decimalSeparator
 * @returns {string}
 */
function formatNumber(cents, decimals, thousands, decimalSeparator) {
  const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
  const grouped = (whole ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return fraction ? `${grouped}${decimalSeparator}${fraction}` : grouped;
}

if (!customElements.get('quantity-breaks-component')) {
  customElements.define('quantity-breaks-component', QuantityBreaksComponent);
}
